import { themeColor } from "@/shared/design-system";

/**
 * La Main sur la carte : une MOTO VUE DE DESSUS, avec son conducteur.
 *
 * ## Pourquoi une image, et non un symbole vectoriel du SDK
 * Les autres marqueurs de cette carte sont des `google.maps.Symbol` — un `path`
 * SVG, une couleur de remplissage, une rotation native. C'est parfait pour un
 * disque ou une flèche, et c'était le cas de l'ancien marqueur.
 *
 * Mais un `Symbol` ne porte QU'UNE couleur de remplissage et QU'UNE couleur de
 * trait. Une moto avec un conducteur en a besoin de trois au minimum — le
 * châssis sombre, le blouson à la couleur de la marque, le halo blanc qui la
 * détache des tuiles — sans quoi elle se réduit à une silhouette qu'on ne lit
 * pas à la taille d'un marqueur.
 *
 * On passe donc par une `google.maps.Icon` : une image SVG en `data:` URI.
 *
 * ## Le prix à payer, et comment il est payé
 * Une icône image ne connaît PAS `rotation` : c'est une propriété des symboles
 * vectoriels seuls. Le cap doit donc être cuit dans l'image, ce qui veut dire
 * en régénérer une par angle.
 *
 * Deux mesures rendent cela gratuit :
 *
 *   • le cap est QUANTIFIÉ à 3° — 120 images possibles au total, là où
 *     l'animation en demanderait une par frame. Trois degrés sont invisibles à
 *     l'œil sur un marqueur de 40 px ;
 *   • chaque image est MISE EN CACHE à sa première génération.
 *
 * Au bout de quelques virages, plus une seule chaîne n'est construite : le
 * suivi tourne sur un jeu d'icônes déjà prêtes.
 *
 * ## Pourquoi la moto tient dans le cadre quoi qu'il arrive
 * L'image tourne autour de son centre. Un dessin qui remplit le carré sort donc
 * du cadre en diagonale et se fait rogner vers 45° — la roue arrière
 * disparaîtrait dans un virage, et seulement là, ce qui est le pire des bogues
 * à diagnostiquer.
 *
 * Le dessin doit tenir dans le CERCLE inscrit. Aux cotes naturelles il mesurait
 * 32,8 de demi-diagonale pour un rayon de 32 : il passait à peine à l'aplomb, et
 * pas du tout de biais. `FIT` le rétrécit autour de son centre jusqu'à
 * 30,3 — la marge qui manquait. Vérifié aux huit points cardinaux, rendu à la
 * taille réelle du marqueur.
 */

/** Côté du cadre, en unités SVG. Le dessin tourne autour de son centre. */
const BOX = 64;
const C = BOX / 2;

/**
 * Taille du marqueur à l'écran, en pixels CSS.
 *
 * 40 px : assez grand pour qu'on distingue le conducteur du châssis sur un
 * téléphone, assez petit pour ne pas masquer la rue qu'il parcourt.
 */
const RENDER_PX = 40;

/** Pas de quantification du cap, en degrés. */
const HEADING_STEP = 3;

/**
 * Rétrécissement du dessin, autour de son centre, pour qu'il tienne dans le
 * cercle inscrit quel que soit l'angle. Cf. l'en-tête du fichier.
 */
const FIT = 0.90;

/**
 * Le dessin, pointant vers le NORD à cap 0.
 *
 * Vue de dessus, de l'arrière vers l'avant : roue arrière, châssis, guidon,
 * bras, buste, casque. L'ordre des formes EST l'ordre d'empilement — le casque
 * passe au-dessus du buste, comme dans la réalité vue du ciel.
 */
function shapes(chassis: string, rider: string, visor: string, lamp: string): string {
  return [
    // Roue arrière, LARGE. Sa largeur est ce qui distingue l'arrière de l'avant
    // quand le marqueur est vu de loin, avant même qu'on lise la visière.
    `<rect x="26.5" y="41" width="11" height="15" rx="4.5" fill="${chassis}"/>`,
    // Roue avant, étroite et allongée.
    `<rect x="29.5" y="6" width="5" height="12" rx="2.5" fill="${chassis}"/>`,
    // Châssis, de l'une à l'autre.
    `<rect x="29" y="15" width="6" height="29" rx="3" fill="${chassis}"/>`,
    // Guidon, avec ses poignées : sans elles, les bras se lisaient comme des
    // antennes et l'ensemble évoquait un insecte plutôt qu'une moto.
    `<rect x="18" y="21" width="28" height="3.6" rx="1.8" fill="${chassis}"/>`,
    `<circle cx="19" cy="22.8" r="2.9" fill="${chassis}"/>`,
    `<circle cx="45" cy="22.8" r="2.9" fill="${chassis}"/>`,
    // Bras : des épaules aux poignées. Ce sont eux qui font lire « quelqu'un
    // conduit » plutôt que « une moto est garée là ».
    `<path d="M25.5 31 L20.5 24.5" stroke="${rider}" stroke-width="4.2" stroke-linecap="round" fill="none"/>`,
    `<path d="M38.5 31 L43.5 24.5" stroke="${rider}" stroke-width="4.2" stroke-linecap="round" fill="none"/>`,
    // Buste : épaules LARGES à l'avant, taille étroite à l'arrière. Cette
    // dissymétrie porte le sens de la marche à elle seule, même si la visière
    // devient illisible au dézoom.
    `<path d="M23 32 q0 -5 9 -5 q9 0 9 5 l-1.6 11 q-0.4 3 -7.4 3 q-7 0 -7.4 -3 z" fill="${rider}"/>`,
    // Casque.
    `<circle cx="${C}" cy="28" r="7.2" fill="${chassis}"/>`,
    // Visière, en bandeau clair sur la moitié AVANT du casque.
    `<path d="M25.4 25.6 a7.2 7.2 0 0 1 13.2 0 q-6.6 3 -13.2 0 z" fill="${visor}"/>`,
    // Phare, à la pointe : le dernier repère qui survit aux petites tailles.
    `<rect x="30.2" y="5.2" width="3.6" height="2.6" rx="1.3" fill="${lamp}"/>`,
  ].join("");
}

/**
 * Le SVG complet pour un cap donné.
 *
 * Le halo blanc est obtenu en dessinant les MÊMES formes en dessous, épaissies
 * au trait. C'est ce qui détache la moto aussi bien d'une route claire que des
 * tuiles assombries du thème sombre — sans lui, le châssis se perd sur l'un et
 * le blouson sur l'autre.
 */
function svgFor(headingDeg: number): string {
  const chassis = "#1f2532";
  const rider = themeColor("or500");
  const visor = "#a9c4e8";
  const lamp = "#fff6cc";
  const halo = "#ffffff";

  const body = shapes(chassis, rider, visor, lamp);
  const outline = shapes(halo, halo, halo, halo);

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${BOX}" height="${BOX}" viewBox="0 0 ${BOX} ${BOX}">` +
    `<g transform="rotate(${headingDeg} ${C} ${C})">` +
    `<g transform="translate(${C} ${C}) scale(${FIT}) translate(${-C} ${-C})">` +
    // Halo : mêmes formes, tracées large, dessous.
    `<g stroke="${halo}" stroke-width="5" stroke-linejoin="round" stroke-linecap="round">${outline}</g>` +
    `<g>${body}</g>` +
    `</g></g></svg>`
  );
}

/**
 * Cache des icônes déjà construites, par cap quantifié.
 *
 * Vidé au changement de thème : la couleur du blouson vient de `themeColor()`,
 * résolue à l'instant de la construction. Sans purge, la moto garderait l'or du
 * thème précédent.
 */
const cache = new Map<number, string>();

export function clearRiderIconCache(): void {
  cache.clear();
}

/** Cap ramené au pas de quantification, dans [0, 360). */
export function quantizeHeading(headingDeg: number): number {
  const step = Math.round(headingDeg / HEADING_STEP) * HEADING_STEP;
  return ((step % 360) + 360) % 360;
}

function dataUri(headingDeg: number): string {
  const cached = cache.get(headingDeg);
  if (cached) return cached;
  // `encodeURIComponent` et non base64 : plus court, lisible au débogage, et
  // accepté partout comme `data:` URI.
  const uri = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgFor(headingDeg))}`;
  cache.set(headingDeg, uri);
  return uri;
}

/**
 * L'icône de la Main, orientée dans le sens de la marche.
 *
 * ⚠️ N'appeler qu'APRÈS `loadGoogleMaps()` : `Size` et `Point` sont des valeurs
 * du SDK, absentes tant que le script n'est pas chargé.
 *
 * L'ancrage est le CENTRE de l'image, et non son coin : le point GPS est sous
 * le conducteur. Avec l'ancrage par défaut, la moto serait décalée d'une
 * demi-largeur et roulerait à côté de sa propre trajectoire.
 */
export function riderIcon(headingDeg: number): google.maps.Icon {
  return {
    url: dataUri(quantizeHeading(headingDeg)),
    scaledSize: new google.maps.Size(RENDER_PX, RENDER_PX),
    anchor: new google.maps.Point(RENDER_PX / 2, RENDER_PX / 2),
  };
}
